/**
 * Permission checks used across the app.
 *
 * Every check goes through the pure evaluators in `evaluate.ts`; this module only binds them
 * to the live sources (selected role, loaded role permissions, catalogue switch, kill switch).
 */

import { useMemo } from "react";
import { getPermissionCatalogue } from "@/env-variables";
import { isPermissionSystemEnabled } from "@/lib/utils/permissionDebug";
import { useAdditionalInfo } from "@/stores/domains/additionalInfo/useAdditionalInfo";
import { getRolePermissions, useRolePermissions } from "@/stores/domains/user/userStore";
import {
  evaluateActionPermission,
  evaluatePagePermission,
  evaluateRoleListPermission,
  type PermissionByRole,
  type PermissionContext,
  type PermissionRoleList,
} from "./evaluate";
import { PREMISSIONS_BY_ROLE } from "./permission.config";

export type { PermissionByRole, PermissionRoleList } from "./evaluate";

/**
 * Get the roles the session runs under (non-hook version for use outside React components)
 * @returns The selected role as a one-element list, or an empty list before one is chosen
 */
export const getUserRoleData = (): PermissionByRole[] => {
  const roleData = useAdditionalInfo.getState().role;

  if (!roleData) return [];

  // If roleData is an array, return it directly
  if (Array.isArray(roleData)) {
    return roleData;
  }

  // If roleData is an object, convert it to array format
  if (typeof roleData === "object") {
    return [
      {
        label: roleData.label,
        value: roleData.value,
      },
    ];
  }

  return [];
};

/**
 * Build the evaluation context from the live stores (non-hook version)
 * @returns Catalogue, kill switch, selected roles and the role's granted permissions
 */
const getPermissionContext = (): PermissionContext => ({
  catalogue: getPermissionCatalogue(),
  permissionSystemEnabled: isPermissionSystemEnabled(),
  userRoles: getUserRoleData(),
  rolePermissions: getRolePermissions(),
});

/**
 * Get the role list of one page key, tagged with the key so v2 can resolve its permission
 * @param role Page key from `PREMISSIONS_BY_ROLE`
 * @returns The page's role list, carrying `pageKey`
 */
export function getPermissionsByRole(role: keyof typeof PREMISSIONS_BY_ROLE): PermissionRoleList {
  const roles = [...(PREMISSIONS_BY_ROLE[role] as PermissionByRole[])] as PermissionRoleList;
  roles.pageKey = role as string;
  return roles;
}

/**
 * Check whether a page may be reached (non-hook version)
 * @param permission v2 permission name, or the legacy page key it replaced
 * @returns Whether the page is allowed
 */
export const hasPagePermission = (permission: string): boolean =>
  evaluatePagePermission(permission, getPermissionContext());

/**
 * Check a navigation entry's role list (non-hook version)
 * Used by `generateRouterConfig` and the sidebar filter, outside React components.
 * @param requiredPermissions Role list from `getPermissionsByRole`, or an inline role list
 * @returns Whether the entry is visible/reachable
 */
export function hasPermissionForRole(
  requiredPermissions: PermissionRoleList | PermissionByRole | PermissionByRole[],
): boolean {
  return evaluateRoleListPermission(requiredPermissions, getPermissionContext());
}

/**
 * Permission helpers for React components
 * @returns `hasPermission` (role comparison), `hasPageAccess` and `hasPermissionByKey`
 */
export const usePermission = () => {
  const role = useAdditionalInfo((state) => state.role);
  const rolePermissions = useRolePermissions();

  const context = useMemo(
    (): PermissionContext => ({
      catalogue: getPermissionCatalogue(),
      permissionSystemEnabled: isPermissionSystemEnabled(),
      userRoles: role ? [{ label: role.label, value: role.value }] : [],
      rolePermissions,
    }),
    [role, rolePermissions],
  );

  const userPermissions = context.userRoles;

  const hasPermission = useMemo(
    () => (perm: PermissionByRole) => evaluateRoleListPermission(perm, context),
    [context],
  );

  const hasPageAccess = useMemo(
    () => (requiredPermissions: PermissionRoleList | PermissionByRole[]) =>
      evaluateRoleListPermission(requiredPermissions, context),
    [context],
  );

  const hasPermissionByKey = useMemo(
    () => (key: string) => evaluatePagePermission(key, context),
    [context],
  );

  return { hasPermission, hasPageAccess, userPermissions, hasPermissionByKey };
};

/**
 * Check a button or field level permission
 * @param action v2 permission name, or the legacy action key it replaced
 * @returns Whether the action is allowed for the selected role
 */
export const useActionPermission = (action: string): boolean => {
  const role = useAdditionalInfo((state) => state.role);
  const rolePermissions = useRolePermissions();

  const userRoles = useMemo((): PermissionByRole[] => {
    if (!role) return [];
    if (Array.isArray(role)) return role;
    return [{ label: role.label, value: role.value }];
  }, [role]);

  return useMemo(
    () =>
      evaluateActionPermission(action, {
        catalogue: getPermissionCatalogue(),
        permissionSystemEnabled: isPermissionSystemEnabled(),
        userRoles,
        rolePermissions,
      }),
    [action, userRoles, rolePermissions],
  );
};
